import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { SLIDE_ID_QUERY_PARAM } from '@/modules/presentation/clientPresentation/ClientPresentationDesigner.utils';
import { useCreatePresentation } from '@/modules/presentation/hooks/useCreatePresentation';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';

import {
  makeAddNotesCustomBundleInput,
  selectAppendedCustomBundle,
} from '../EntityNotesPerDeath.util';
import { useAddCustomSlideToPresentationMutation } from '../graphql/AddNotesToPresentation.generated';

interface AddNotesToPresentationInput {
  presentationId?: string;
  onSuccess: () => void;
}

export function useAddNotesToPresentation({
  householdId,
  entityDisplayName,
  notes,
}: {
  householdId: string;
  entityDisplayName: string;
  notes: string;
}) {
  const navigate = useNavigate();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { createPresentation, loading: isCreatingPresentation } =
    useCreatePresentation(householdId, {
      navigateOnCreate: false,
    });
  const [addCustomSlide] = useAddCustomSlideToPresentationMutation();

  const [isSaving, setIsSaving] = useState(false);
  // A ref (not just `isSaving`) guards re-entrancy: a rapid double-click can
  // fire two calls in the same tick — before the isSaving re-render disables
  // the buttons — which on the "create new" path would create two presentations.
  const inFlightRef = useRef(false);

  const addNotesToPresentation = useCallback(
    async ({ presentationId, onSuccess }: AddNotesToPresentationInput) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setIsSaving(true);
      try {
        let targetPresentationId = presentationId;
        if (!targetPresentationId) {
          const result = await createPresentation();
          targetPresentationId = result.data?.createBlankClientPresentation.id;
          if (!targetPresentationId) {
            // useCreatePresentation registers an onError handler, so a failed
            // mutation resolves (rather than rejects) with no data after having
            // already reported and surfaced its own error feedback.
            return;
          }
        }

        const result = await addCustomSlide({
          variables: {
            input: {
              id: targetPresentationId,
              update: {},
              withBundles: [
                makeAddNotesCustomBundleInput({
                  householdId,
                  entityDisplayName,
                  notes,
                }),
              ],
            },
          },
        });

        const bundles =
          result.data?.updateClientPresentationV2.bundles.edges
            ?.map((edge) => edge?.node)
            .filter(
              (bundle): bundle is NonNullable<typeof bundle> => !!bundle
            ) ?? [];
        const appendedBundle = selectAppendedCustomBundle(bundles);

        if (!appendedBundle) {
          throw new Error('The new presentation slide could not be found');
        }

        onSuccess();
        showFeedback('Added to presentation', { variant: 'success' });
        const presentationPath = getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATION_V2,
          {
            householdId,
            presentationId: targetPresentationId,
          }
        );
        navigate(
          `${presentationPath}?${SLIDE_ID_QUERY_PARAM}=${encodeURIComponent(appendedBundle.id)}`
        );
      } catch (error) {
        reportError('Failed to add notes to presentation', error as Error, {
          householdId,
        });
        showFeedback('Failed to add notes to presentation. Please try again.', {
          variant: 'error',
        });
      } finally {
        inFlightRef.current = false;
        setIsSaving(false);
      }
    },
    [
      addCustomSlide,
      createPresentation,
      entityDisplayName,
      householdId,
      navigate,
      notes,
      reportError,
      showFeedback,
    ]
  );

  return {
    addNotesToPresentation,
    loading: isSaving || isCreatingPresentation,
  };
}
